import {
	AEMContent,
	Card,
	FieldWrapper,
	Paragraph,
	Select,
} from '@mlc/web-ui-toolkit';
import React, { FunctionComponent, useEffect, useState } from 'react';
import {
	Category,
	PlanDetails as PlanDetailsDomain,
} from '../../../../../logic/domains/planProfile.domain';
import { getPlanDetails } from '../../../../../logic/functions/planProfile.function';
import { asPercentage } from '../../../../common/formatter';
import LoadingIndicator from '../../../../common/LoadingIndicator';
import { ZeroMargin } from '../../../Common/styled';
import ApiError from '../../../Error/ApiError';
import DownloadCsv from '../Common/DownloadCsv';
import Tab from '../Common/Tab';
import planProfileTestIds from '../PlanProfile.elements';
import PlanCategory from './PlanCategory';

type Props = {
	planUUID: string;
	planNumber: string;
	planName: string;
	productName: string;
};

const filterOpions = [
	{ label: 'All', value: 'All' },
	{ label: 'Open', value: 'Open' },
	{ label: 'Closed', value: 'Closed' },
];

const PlanDetails: FunctionComponent<Props> = ({
	planUUID,
	planNumber,
	planName,
	productName,
}) => {
	const [planDetails, setPlanDetails] = useState<
		PlanDetailsDomain | undefined
	>();
	const [accordions, setAccordions] = useState<boolean[]>([false]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedStatus, setSelectedStatus] = useState<string>(
		filterOpions[0].value
	);

	const handleBenefitGroupFilter = (event: any) => {
		setSelectedStatus(event.target.value);
	};

	const byStatus = (category: Category) => {
		if (selectedStatus != 'All') {
			return category.status == selectedStatus && category.members > 0;
		} else {
			return category.members > 0;
		}
	};

	const withMembers = (category: Category) => category.members > 0;

	const byCategoryStatusAndName = (a: Category, b: Category) => {
		if (a.status == 'Open' && b.status != 'Open') {
			return -1;
		}

		if (a.status != 'Open' && b.status == 'Open') {
			return 1;
		}

		return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
	};

	const updateAccordionsState = (idx: number) =>
		setAccordions(
			accordions.map((value: boolean, i: number) =>
				idx === i ? !value : false
			)
		);

	useEffect(() => {
		const fetchData = async () => {
			const planDetails = await getPlanDetails(planUUID);
			setPlanDetails(planDetails);
			if (planDetails) {
				setAccordions(planDetails.planCategories.map(c => false));
			}
			setIsLoading(false);
		};
		fetchData();
	}, []);

	if (isLoading) return <LoadingIndicator />;

	if (!planDetails) return <ApiError />;

	const statusWithMembers = planDetails.planCategories
		.filter(byStatus)
		.map((c: Category) => {
			return {
				...c,
				incomeProtectionInsurance: c.incomeProtectionInsurance
					? c.incomeProtectionInsurance
					: 'N/A',
			};
		});

	const hasMembers = planDetails.planCategories.filter(withMembers);

	const categories = statusWithMembers
		.sort(byCategoryStatusAndName)
		.map((category: Category, idx: number) => (
			<PlanCategory
				key={idx}
				category={category}
				isOpen={accordions[idx]}
				onChange={() => updateAccordionsState(idx)}
			/>
		));

	return (
		<Tab title="Plan Details">
			{hasMembers.length === 0 ? (
				<NoMembers />
			) : (
				<>
					<AEMContent path="/secure/_content/Adviser/plan-profile/plan-details/introduction" />
					<Card
						justifyContent="space-between"
						display="flex"
						alignItems="center"
					>
						<FieldWrapper label="Filter Status:">
							<Select
								onChange={handleBenefitGroupFilter}
								value={selectedStatus}
								options={filterOpions}
								data-id={planProfileTestIds.planDetailsFilter}
							/>
						</FieldWrapper>
						<DownloadCsv
							fetchDataToDownload={() => statusWithMembers}
							options={{
								fileName: `Plan_details_${planUUID}`,
								title: [
									`Plan Name,${planName}`,
									`Plan No.,${planNumber}`,
									`Product Name,${productName}`,
								],
								fields: CsvFields,
							}}
						/>
					</Card>
					{categories}
					<CommissionValue value={planDetails.commissionValue} />
				</>
			)}
		</Tab>
	);
};

const NoMembers = () => (
	<ZeroMargin>
		<AEMContent path="/secure/_content/Adviser/plan-profile/plan-details/no-members" />
	</ZeroMargin>
);

const CommissionValue = ({ value }: { value: number }) =>
	value > 0 ? (
		<Paragraph
			fontSize="disclaimer"
			marginTop={6}
			marginBottom={0}
			data-id={planProfileTestIds.planCategoriesDisclaimer}
		>
			Insurance commission does not apply to members who join MasterKey Business
			Super after MySuper commences or who are invested entirely in the MySuper
			investment option. ({asPercentage(value)})
		</Paragraph>
	) : null;

const CsvFields = [
	{
		name: 'name',
		header: 'Benefit Group',
	},
	{ name: 'status' },
	{ name: 'withdrawalValue' },
	{ name: 'feesRule', header: 'Fees' },
	{ name: 'deathInsurance' },
	{ name: 'totalAndPermanentDisabilityInsurance' },
	{ name: 'incomeProtectionInsurance' },
	{ name: 'members' },
];

export default PlanDetails;
